import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type User, UserApi, type UserConnectedProjection } from '@/api-swagger'
import { UserRole } from '@/utils/enums'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { useSuiteStore } from '@/stores/useSuiteStore'

export const useUserStore = defineStore('user', () => {
  type Commercial = {
    id: number
    name: string
    email: string
  }
  const userConnected = ref<UserConnectedProjection | null>(null)
  const user = ref<User | null>(null)
  const users = ref<User[] | null>(null)

  const myTeamStore = useMyTeamStore()

  const role = computed(() => {
    const boostCoreStore = useBoostCorePermissionStore()
    return boostCoreStore.isManager ? UserRole.Manager : UserRole.Commercial
  })

  /**
   * Liste des commerciaux managés (manager + équipe), dérivée de useMyTeamStore.
   * Les membres sans odoo16Id sont filtrés (impossible d'appeler l'API Odoo sans cet ID).
   */
  const commercials = computed<Commercial[]>(() =>
    myTeamStore.all
      .map((m) => {
        const id = m.metadata?.odoo16Id
        if (typeof id !== 'number') return null
        return {
          id,
          name: myTeamStore.memberLabel(m),
          email: m.email,
        }
      })
      .filter((c): c is Commercial => c !== null),
  )

  const commercialsIds = computed<number[]>(() =>
    commercials.value.map((c) => c.id),
  )

  const api = new UserApi()
  const error = ref<string | null>(null)

  /**
   * @deprecated useUserStore.commercials est désormais dérivée de useMyTeamStore.
   * Conservée temporairement comme no-op pour la compatibilité ascendante.
   */
  function enrichCommercialsFromKpiTeam(_kpiTeam: { id: number; name: string; email: string }[]) {
    // no-op: la source unique est désormais useMyTeamStore (alimentée par /users/me/team)
  }

  function ensureMyTeamLoaded() {
    const boostCoreStore = useBoostCorePermissionStore()
    if (boostCoreStore.isManager && !myTeamStore.isLoaded && !myTeamStore.isLoading) {
      myTeamStore.fetch().catch((e) => console.error('myTeamStore.fetch failed', e))
    }
  }

  const getUserByEmail = async (email: string | undefined) => {
    if (!email) return null
    try {
      const result = await api.getUserByEmail({ email: email })
      userConnected.value = result[0] || null
      ensureMyTeamLoaded()
      return result[0]
    } catch (err) {
      console.error('Erreur API :', err)
      error.value = 'Erreur récupération utilisateur'
    }
  }

  const getMe = async () => {
    try {
      const result = await api.getMyUser()
      userConnected.value = result[0] || null
      ensureMyTeamLoaded()
      return result[0]
    } catch (err) {
      console.error('Erreur API :', err)
      error.value = 'Erreur récupération utilisateur'
    }
  }

  const getUserToken = async () => {
    try {
      // Use the API without custom headers - the DefaultConfig will apply DPoP headers
      return await api.getToken()
    } catch (err) {
      console.error('Erreur API :', err)
      error.value = 'Erreur récupération utilisateur'
    }
  }

  const getAllUsers = async () => {
    const result = await api.getAllUsers()
    users.value = result
    return result
  }

  const getLocalInfoUserById = (id: number): User | null => {
    try {
      return (
        users.value?.filter((userFilter) => {
          return userFilter.id == id
        })[0] || null
      )
    } catch (e) {
      console.error(e)
      return null
    }
  }

  /** Email du commercial pour un user_id Odoo (crm.lead) : cache users, puis useMyTeamStore. */
  const getCommercialEmailByOdooUserId = (odooUserId: number): string => {
    const fromList = getLocalInfoUserById(odooUserId)?.email
    if (fromList) return String(fromList).trim()

    const fromTeam = myTeamStore.findByOdooId(odooUserId)
    if (fromTeam?.email) return fromTeam.email.trim()

    return ''
  }

  const getLocalInfoByPartnerId = (id: number): User | null => {
    try {
      return (
        users.value?.filter((userFilter) => {
          return userFilter.partnerId?.id == id
        })[0] || null
      )
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const getLocalInfoByPartnerEmail = (email: string): User | null => {
    try {
      return (
        users.value?.filter((userFilter) => {
          return userFilter.email == email
        })[0] || null
      )
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const isManager = computed(() => {
    return role.value === UserRole.Manager
  })

  /**
   * Sync mes infos Odoo dans mes metadata. La source est résolue côté boostcore
   * (FF puis BU). Préserve les autres clés (ex: garde odoo16Id en ajoutant odoo18Id).
   */
  const syncMyOdoo = async () => {
    const suiteStore = useSuiteStore()
    if (suiteStore.isSuiteAvailable === false) return null
    try {
      const res = await suiteStore.suiteFetch('users/me/sync-odoo', {
        method: 'POST',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const data = await res.json()
        // L'équipe peut maintenant être enrichie avec le nouvel ID Odoo de la source courante.
        await myTeamStore.fetch().catch(() => undefined)
        return data
      }
    } catch (err) {
      console.error('syncMyOdoo failed', err)
      error.value = 'Erreur sync Odoo'
    }
    return null
  }

  return {
    userConnected,
    user,
    users,
    role,
    commercials,
    commercialsIds,
    getLocalInfoUserById,
    getCommercialEmailByOdooUserId,
    getUserByEmail,
    getAllUsers,
    getMe,
    getUserToken,
    getLocalInfoByPartnerEmail,
    getLocalInfoByPartnerId,
    isManager,
    enrichCommercialsFromKpiTeam,
    syncMyOdoo,
    errorUserStore: error,
  }
})
